import { Capacitor } from "@capacitor/core";

/**
 * Two auth paths, one API.
 *
 * Web runs the BFF pattern: the backend is the confidential OIDC client, holds
 * the tokens, and hands the browser an HttpOnly cookie. Nothing here touches a
 * token — signing in is just a navigation to /api/auth/login.
 *
 * Native cannot share that cookie (the WebView is a different origin from the
 * system browser), so it is a public PKCE client in its own right: system
 * browser for the authorization request, custom-scheme deep link back, and the
 * code exchanged straight against Authelia.
 */

const TOKEN_STORAGE_KEY = "sieve.oidc.tokens";
const PKCE_STORAGE_KEY = "sieve.oidc.pkce";
const SIGN_OUT_REASON_KEY = "sieve.oidc.sign-out-reason";
/** Refresh a little early so a request never races the expiry. */
const REFRESH_SKEW_MS = 60 * 1000;

export const isNative = (): boolean => Capacitor.isNativePlatform();

const issuer = (): string =>
  (import.meta.env.VITE_OIDC_ISSUER as string | undefined) ?? "https://auth.piwo.app";

const nativeClientId = (): string =>
  (import.meta.env.VITE_OIDC_NATIVE_CLIENT_ID as string | undefined) ?? "sieve-native";

const nativeRedirectUri = (): string =>
  (import.meta.env.VITE_OIDC_NATIVE_REDIRECT_URI as string | undefined) ??
  "app.piwo.sieve:/oauth/callback";

const scopes = (): string =>
  (import.meta.env.VITE_OIDC_SCOPES as string | undefined) ??
  "openid profile email groups offline_access";

interface StoredTokens {
  accessToken: string;
  refreshToken?: string;
  expiresAt: number;
}

export interface SignOutReason {
  reason: string;
  at: number;
}

/**
 * The token endpoint could not be asked, as opposed to answering "no". The
 * caller has to keep the credentials in that case: a provider that is
 * restarting, or a proxy returning 502 for a few seconds, is not a revoked
 * session, and treating it as one signs the user out for an outage they never
 * saw.
 */
export class RefreshUnavailableError extends Error {}

interface PendingLogin {
  state: string;
  codeVerifier: string;
  redirectTo?: string;
  startedAt: number;
}

/**
 * How long a started native sign-in stays authoritative. Long enough to walk
 * through the portal, short enough that an abandoned attempt does not block a
 * retry forever.
 */
const LOGIN_IN_FLIGHT_MS = 10 * 60 * 1000;

/**
 * The app has to be back in the foreground for this long before a sign-in that
 * is still pending counts as abandoned. The callback deep link arrives within
 * milliseconds of the resume, so anything still unfinished after the grace
 * period was dismissed rather than completed.
 */
const ABANDONED_GRACE_MS = 3 * 1000;

/** Event the app fires when a sign-in has to be started by the user. */
export const AUTH_REQUIRED_EVENT = "sieve:auth-required";

/** When the app last came back to the foreground; 0 until it ever has. */
let lastResumedAt = 0;

/**
 * Told by the app whenever it becomes active again. Returning from the system
 * browser without a callback is the only signal that the user backed out of a
 * sign-in — nothing else distinguishes it from one still in progress.
 */
export const noteAppResumed = (): void => {
  lastResumedAt = Date.now();
};

/**
 * A pending sign-in nobody is going to finish: either it aged out, or the app
 * has been back in the foreground since it started and no callback followed.
 */
const isAbandoned = (login: PendingLogin): boolean => {
  if (Date.now() - login.startedAt >= LOGIN_IN_FLIGHT_MS) return true;
  return lastResumedAt > login.startedAt && Date.now() - lastResumedAt > ABANDONED_GRACE_MS;
};

const readJson = <T>(key: string): T | undefined => {
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : undefined;
  } catch {
    return undefined;
  }
};

const writeJson = <T>(key: string, value: T): void => {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Storage can be unavailable in private modes; the user just re-authenticates.
  }
};

const clearKey = (key: string): void => {
  try {
    window.localStorage.removeItem(key);
  } catch {
    // ignore
  }
};

/**
 * Records why the app threw its credentials away. A native sign-out is
 * otherwise completely silent: the tokens vanish, the next request 401s, and
 * nothing anywhere says whether the provider refused the grant or never issued
 * a refresh token to begin with.
 */
const noteSignedOut = (reason: string): void => {
  writeJson<SignOutReason>(SIGN_OUT_REASON_KEY, { reason, at: Date.now() });
};

/** Why the app last discarded its credentials, for the sign-in banner. */
export const readSignOutReason = (): SignOutReason | undefined =>
  readJson<SignOutReason>(SIGN_OUT_REASON_KEY);

const base64Url = (bytes: Uint8Array): string => {
  let binary = "";
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
};

const randomString = (): string => base64Url(crypto.getRandomValues(new Uint8Array(32)));

const codeChallengeFor = async (verifier: string): Promise<string> => {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier));
  return base64Url(new Uint8Array(digest));
};

/** True when a deep link is our OAuth callback rather than a shared link. */
export const isOAuthCallbackUrl = (url: string): boolean => {
  const redirect = nativeRedirectUri();
  const base = redirect.split("?")[0];
  return url.startsWith(base);
};

/**
 * Outcome of asking for a sign-in: either one just began, or an earlier attempt
 * still owns the flow and the caller has to tell the user so.
 */
export type LoginStart = "started" | "in-progress";

/**
 * Starts a sign-in. Resolves only on web, where it navigates away.
 *
 * Pass `force` for an explicit user action such as a retry button; automatic
 * callers (the 401 handler) must not, so that concurrent failures cannot
 * restart a sign-in that is already under way.
 */
export const beginLogin = async (
  redirectTo?: string,
  options?: { force?: boolean },
): Promise<LoginStart> => {
  const current = `${window.location.pathname}${window.location.search}`;
  // Never send an /api path back as the post-login destination. The app has no
  // routes there, and feeding /api/auth/login to itself nests the redirect
  // parameter on every pass — an infinite loop rather than a sign-in.
  const requested = redirectTo ?? current;
  const target = requested.startsWith("/api/") ? "/" : requested;

  if (!isNative()) {
    window.location.assign(`/api/auth/login?redirect=${encodeURIComponent(target)}`);
    return "started";
  }

  // Several requests can fail with 401 at the same moment on start-up. On web
  // the navigation above ends the page, so only one sign-in ever begins; on
  // native nothing interrupts the others, and each would overwrite the PKCE
  // belonging to the request the user is actually completing in the browser.
  // The first attempt wins until it is finished, abandoned, or expires.
  const inFlight = readJson<PendingLogin>(PKCE_STORAGE_KEY);
  if (!options?.force && inFlight && !isAbandoned(inFlight)) {
    return "in-progress";
  }

  const codeVerifier = randomString();
  const state = randomString();
  writeJson<PendingLogin>(PKCE_STORAGE_KEY, {
    state,
    codeVerifier,
    redirectTo: target,
    startedAt: Date.now(),
  });

  const url = new URL("/api/oidc/authorization", issuer());
  url.searchParams.set("client_id", nativeClientId());
  url.searchParams.set("response_type", "code");
  url.searchParams.set("redirect_uri", nativeRedirectUri());
  url.searchParams.set("scope", scopes());
  url.searchParams.set("state", state);
  url.searchParams.set("code_challenge", await codeChallengeFor(codeVerifier));
  url.searchParams.set("code_challenge_method", "S256");

  // RFC 8252 requires an external user agent, never an embedded WebView: the
  // system browser already holds the Authelia session, and an in-app WebView
  // could read the user's credentials as they type them.
  //
  // "_system" is the Capacitor/Cordova convention for "hand this to the device
  // browser". If the WebView refuses to open a window we fall back to a plain
  // navigation, which Capacitor also routes externally for off-origin URLs.
  // Worth confirming on a device — if neither path leaves the app, install
  // @capacitor/browser and call Browser.open() here instead.
  const opened = window.open(url.toString(), "_system");
  if (!opened) {
    window.location.href = url.toString();
  }
  return "started";
};

/**
 * Finishes the native flow from the deep link Authelia redirected to.
 * Returns the in-app path to resume, if one was recorded.
 */
export const completeNativeLogin = async (callbackUrl: string): Promise<string | undefined> => {
  const pending = readJson<PendingLogin>(PKCE_STORAGE_KEY);
  clearKey(PKCE_STORAGE_KEY);
  if (!pending) throw new Error("No sign-in was in progress on this device.");

  // Custom-scheme URLs parse inconsistently, so read the query off the raw string.
  const query = new URLSearchParams(callbackUrl.slice(callbackUrl.indexOf("?") + 1));
  const error = query.get("error");
  if (error) throw new Error(`Sign-in was rejected: ${error}`);

  const code = query.get("code");
  if (!code) throw new Error("The sign-in callback carried no authorization code.");
  if (query.get("state") !== pending.state) {
    throw new Error("The sign-in callback did not match the request that started it.");
  }

  const response = await fetch(new URL("/api/oidc/token", issuer()).toString(), {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: new URLSearchParams({
      grant_type: "authorization_code",
      code,
      redirect_uri: nativeRedirectUri(),
      client_id: nativeClientId(),
      code_verifier: pending.codeVerifier,
    }).toString(),
  });

  if (!response.ok) {
    throw new Error(`Token exchange failed with ${response.status}.`);
  }

  storeTokenResponse((await response.json()) as TokenResponse);
  return pending.redirectTo;
};

interface TokenResponse {
  access_token: string;
  refresh_token?: string;
  expires_in?: number;
}

const storeTokenResponse = (tokens: TokenResponse): void => {
  const existing = readJson<StoredTokens>(TOKEN_STORAGE_KEY);
  writeJson<StoredTokens>(TOKEN_STORAGE_KEY, {
    accessToken: tokens.access_token,
    // Authelia rotates refresh tokens, but a refresh response that carries none
    // leaves the previous one in force — as the backend session store already
    // assumes. Overwriting it with nothing left the app with an access token it
    // could never renew, and a forced sign-in at the next expiry.
    refreshToken: tokens.refresh_token ?? existing?.refreshToken,
    expiresAt: Date.now() + (tokens.expires_in ?? 300) * 1000,
  });
};

/** The refresh this app is currently waiting on, shared by every caller. */
let refreshInFlight: Promise<string | undefined> | undefined;

/**
 * Refresh once, however many requests notice the expiry.
 *
 * The access token lives a day, so the first batch of calls after the app has
 * been idle overnight all find it stale at the same moment. Authelia rotates
 * refresh tokens and treats a second presentation of one as replay: it revokes
 * the whole chain rather than merely refusing the call. Refreshing per request
 * therefore signed the user out every single day — the same defect the backend
 * guard already carries a fix for, on the half of the app that never got one.
 */
const refreshTokens = async (refreshToken: string): Promise<string | undefined> => {
  if (!refreshInFlight) {
    refreshInFlight = performRefresh(refreshToken).finally(() => {
      refreshInFlight = undefined;
    });
  }
  return refreshInFlight;
};

const performRefresh = async (refreshToken: string): Promise<string | undefined> => {
  let response: Response;
  try {
    response = await fetch(new URL("/api/oidc/token", issuer()).toString(), {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: new URLSearchParams({
        grant_type: "refresh_token",
        refresh_token: refreshToken,
        client_id: nativeClientId(),
      }).toString(),
    });
  } catch {
    throw new RefreshUnavailableError("The sign-in service could not be reached.");
  }

  if (!response.ok) {
    // A provider that is restarting, a proxy 502 or a rate limit says nothing
    // about the grant, so the credentials outlive it. Only a refusal — the
    // token is revoked, expired or unknown — is grounds for signing out.
    if (response.status >= 500 || response.status === 408 || response.status === 429) {
      throw new RefreshUnavailableError(`The sign-in service answered ${response.status}.`);
    }

    const body = (await response.json().catch(() => ({}))) as { error?: string };

    // Only give up on the tokens this attempt was actually holding. A refresh
    // that lost a race — or one left over from a previous grant — must not
    // delete credentials a successful refresh has since stored.
    const current = readJson<StoredTokens>(TOKEN_STORAGE_KEY);
    if (!current || current.refreshToken === refreshToken) {
      noteSignedOut(
        `the provider refused to renew the session (${response.status} ${body.error ?? "no reason given"})`,
      );
      clearKey(TOKEN_STORAGE_KEY);
    }
    return undefined;
  }

  const tokens = (await response.json()) as TokenResponse;
  storeTokenResponse(tokens);
  return tokens.access_token;
};

/** The bearer token for native API calls, refreshed on demand. Undefined on web. */
export const getAccessToken = async (): Promise<string | undefined> => {
  if (!isNative()) return undefined;

  const stored = readJson<StoredTokens>(TOKEN_STORAGE_KEY);
  if (!stored) return undefined;

  if (stored.expiresAt - REFRESH_SKEW_MS > Date.now()) return stored.accessToken;
  if (!stored.refreshToken) {
    // Authelia issues one only when the client is granted offline_access and
    // the refresh_token grant. Without it the session cannot outlive its access
    // token however well this app behaves, so say so rather than appearing to
    // forget the sign-in by itself.
    noteSignedOut("the sign-in returned no refresh token, so the session could not outlive its access token");
    clearKey(TOKEN_STORAGE_KEY);
    return undefined;
  }

  return refreshTokens(stored.refreshToken);
};

/**
 * Renews the token after the API has rejected it, whatever this app believed
 * about its expiry.
 *
 * The two ends do not have to agree on when a token died: the phone measures
 * the lifetime from its own clock at issuance, the API reads `exp` from the
 * provider's. A phone running a minute behind, or a grant the provider aged out
 * early, arrives here holding a refresh token that still works perfectly —
 * spending it is far cheaper than sending the user back to the portal.
 *
 * Returns the new access token, or undefined when there is nothing left to try.
 */
export const refreshAfterRejection = async (): Promise<string | undefined> => {
  if (!isNative()) return undefined;

  const stored = readJson<StoredTokens>(TOKEN_STORAGE_KEY);
  if (!stored?.refreshToken) return undefined;

  return refreshTokens(stored.refreshToken);
};

export const isSignedIn = async (): Promise<boolean> =>
  isNative() ? (await getAccessToken()) !== undefined : true;

export const logout = async (): Promise<void> => {
  if (isNative()) {
    clearKey(TOKEN_STORAGE_KEY);
    return;
  }
  await fetch("/api/auth/logout", { method: "POST", credentials: "same-origin" });
  window.location.assign("/");
};
